import type { DocumentExportDialogContentFragment } from "../client/generated";
import { useState } from "react";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { Button } from "@acdc2/ui/components/button";
import { Trans, useTranslation } from "react-i18next";
import { DocumentExportCombobox } from "./DocumentExportCombobox";
import { DocumentExportArtifact } from "./DocumentExportArtifact";
import { assertTypeName } from "../client/guards";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogLoading,
  DialogTitle,
} from "@acdc2/ui/components/dialog";
import {
  ExporterType,
  useCreateArtifactMutation,
  useDocumentExportDialogContentQuery,
} from "../client/generated";

type Props = {
  fragment: DocumentExportDialogContentFragment;
  isOpen: boolean;
};

export function DocumentExportDialogContent({ fragment, isOpen }: Props) {
  const { t } = useTranslation();
  const [exportType, setExportType] = useState<ExporterType>();
  const [createArtifact, { loading }] = useCreateArtifactMutation();

  const { data } = useDocumentExportDialogContentQuery({
    variables: { documentId: fragment.id },
    pollInterval: 3000,
    skip: !isOpen,
  });

  if (!data) return <DialogLoading />;
  const { document } = data;
  assertTypeName(document?.__typename === "Document");

  const exportDocument = async () => {
    if (!exportType) return;
    await createArtifact({
      variables: {
        exportType,
        documentId: document.id,
      },
    });
  };

  return (
    <DialogContent showCloseButton={false}>
      <DialogHeader>
        <DialogTitle>{t("DocumentExportDialogContent.title")}</DialogTitle>
        <DialogDescription>
          <Trans
            i18nKey="DocumentExportDialogContent.description"
            components={{ "word-break": <span className="break-all" /> }}
            values={{ name: document.name }}
          />
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-row gap-2">
        <DocumentExportCombobox value={exportType} onSelect={setExportType} />
        <AccessButton
          variant="outline"
          disabled={!exportType}
          onClick={exportDocument}
          loading={loading}
        >
          {t("DocumentExportDialogContent.submit")}
        </AccessButton>
      </div>

      <div className="flex flex-col gap-3">
        <DialogTitle asChild>
          <h3>{t("DocumentExportDialogContent.artifacts.title")}</h3>
        </DialogTitle>
        {document.artifacts.artifacts.length > 0 ? (
          <ul className="flex flex-col gap-4">
            {document.artifacts.artifacts.map((artifact) => (
              <li key={artifact.id}>
                <DocumentExportArtifact fragment={artifact} />
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-sm text-muted-foreground">
            {t("DocumentExportDialogContent.artifacts.placeholder")}
          </span>
        )}
      </div>

      <DialogFooter>
        <DialogClose asChild>
          <Button type="button">{t("DocumentExportDialogContent.done")}</Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
}
